/**
 * What produced an edit, when it was not simply a hand: an add-on's run (CONTRACTS §19,
 * `collab:label`).
 *
 * An add-on's edit is applied locally, so it stays on this person's undo stack, which makes it an
 * ordinary local update to Yjs. The label is the only thing that says otherwise, and it has to be
 * on the connection while the edit is made: collab tags every update that follows it, on that
 * socket, until the label is cleared.
 */
export interface UpdateLabel {
  readonly origin: 'plugin';
  readonly runId: string;
}

/** The part of a room's provider this needs. */
export interface Labelable {
  readonly provider: { sendStateless(payload: string): void } | null;
}

/**
 * Runs `fn` with `label` on the connection. Synchronous on purpose: Yjs sends each local update at
 * the end of its transaction, inside the call, so the label is in place for exactly the edits it
 * describes and cleared before the next thing typed is a hand's again.
 *
 * A room with no connection (degraded, or the kill switch) has nobody to tell: its edits reach the
 * server through `live.push`, as the person's own.
 */
export function labelled<T>(room: Labelable, label: UpdateLabel, fn: () => T): T {
  const provider = room.provider;
  if (!provider) return fn();
  provider.sendStateless(JSON.stringify({ type: 'collab:label', origin: label.origin, runId: label.runId }));
  try {
    return fn();
  } finally {
    provider.sendStateless(JSON.stringify({ type: 'collab:label', origin: null }));
  }
}
